<script setup lang="ts">
/**
 * Scène du haut des pages produit en charte « apple » (Karim 2026-10-18) :
 * la VRAIE page publique sur un ordinateur sobre (barre d'adresse, socle),
 * et sa version mobile dans un téléphone qui chevauche en bas à droite
 * (parallaxe légère). Utilisée par /site-internet et /boutique.
 *
 * Image ordinateur chargée en priorité (élément le plus grand de l'écran) ;
 * capture mobile en 900 px de large (ratio d'un iPhone, ~0,46).
 */
const props = defineProps<{
  imageOrdinateur: string;
  largeurOrdinateur: number;
  hauteurOrdinateur: number;
  altOrdinateur: string;
  imageTelephone: string;
  largeurTelephone: number;
  hauteurTelephone: number;
  altTelephone: string;
  /** Adresse affichée dans la barre du navigateur. */
  adresse: string;
}>();

const telephone = ref<HTMLElement | null>(null);
useParallaxLeger(telephone, -0.07);
</script>

<template>
  <div class="scene">
    <div class="ordinateur-ecran">
      <div class="ordinateur-barre" aria-hidden="true">
        <span class="ordinateur-points"><i /><i /><i /></span>
        <span class="ordinateur-adresse">
          <UIcon name="i-lucide-lock" class="size-2.5 text-[#6e6e73]" />
          {{ props.adresse }}
        </span>
        <span />
      </div>
      <NuxtImg
        :src="props.imageOrdinateur"
        :alt="props.altOrdinateur"
        :width="props.largeurOrdinateur"
        :height="props.hauteurOrdinateur"
        sizes="xs:92vw sm:92vw md:92vw lg:960px"
        loading="eager"
        fetchpriority="high"
        preload
        class="block h-auto w-full"
      />
    </div>
    <div class="ordinateur-socle" aria-hidden="true"><span /></div>

    <div ref="telephone" class="telephone">
      <TelephoneApple
        :image="props.imageTelephone"
        :largeur="props.largeurTelephone"
        :hauteur="props.hauteurTelephone"
        :alt="props.altTelephone"
      />
    </div>
  </div>
</template>

<style scoped>
.scene {
  position: relative;
  max-width: 1000px;
  margin: 72px auto 0;
  padding: 0 4%;
}
.ordinateur-ecran {
  overflow: hidden;
  padding: 10px 10px 0;
  border-radius: 20px 20px 0 0;
  background: #1d1d1f;
  box-shadow:
    0 0 0 1px rgba(0, 0, 0, 0.04),
    0 40px 90px -30px rgba(0, 0, 0, 0.35);
}
.ordinateur-barre {
  display: grid;
  grid-template-columns: 70px 1fr 70px;
  align-items: center;
  height: 34px;
  padding: 0 12px;
  border-radius: 10px 10px 0 0;
  background: #fbfbfd;
  border-bottom: 1px solid #e8e8ed;
}
.ordinateur-points {
  display: flex;
  gap: 6px;
}
.ordinateur-points i {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #d2d2d7;
}
.ordinateur-adresse {
  justify-self: center;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  overflow: hidden;
  padding: 3px 16px;
  border-radius: 7px;
  background: #e8e8ed;
  font-size: 11px;
  color: #424245;
  letter-spacing: 0;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.ordinateur-socle {
  position: relative;
  width: 108%;
  height: 16px;
  margin-left: -4%;
  border-radius: 0 0 14px 14px;
  background: linear-gradient(#e8e8ed, #c7c7cc);
  box-shadow: 0 18px 30px -18px rgba(0, 0, 0, 0.4);
}
.ordinateur-socle span {
  position: absolute;
  top: 0;
  left: 50%;
  width: 14%;
  height: 6px;
  border-radius: 0 0 8px 8px;
  background: #b3b3b8;
  transform: translateX(-50%);
}

/* Téléphone (cadre : TelephoneApple), posé en bas à droite de l'écran. */
.telephone {
  position: absolute;
  right: 1%;
  bottom: -12%;
  width: 22%;
  will-change: transform;
}

@media (max-width: 640px) {
  .scene {
    margin-top: 48px;
    padding: 0 6%;
  }
  .ordinateur-ecran {
    padding: 6px 6px 0;
    border-radius: 12px 12px 0 0;
  }
  .ordinateur-barre {
    grid-template-columns: 34px 1fr 10px;
    height: 22px;
    padding: 0 8px;
  }
  .ordinateur-points {
    gap: 3px;
  }
  .ordinateur-points i {
    width: 6px;
    height: 6px;
  }
  .ordinateur-adresse {
    padding: 1px 8px;
    font-size: 8px;
  }
  .ordinateur-socle {
    height: 9px;
  }
  .telephone {
    right: 0;
    bottom: -18%;
    width: 27%;
  }
}
@media (prefers-reduced-motion: reduce) {
  .telephone {
    transform: none !important;
  }
}
</style>
